import type { Locale } from "../i18n/locales";
import type { ArticleFaq } from "../shared";

/**
 * The questions people ask about SprinkleDays itself, as opposed to the
 * questions about learning a language that live at the foot of each article.
 *
 * This is the one page that emits `FAQPage` structured data, because it is the
 * one page that genuinely is a list of questions. Google withdrew the FAQ rich
 * result in May 2026, so the node here is a classification rather than a bid
 * for a decoration: it says what the page is, and the answers still have to
 * earn their place by being read.
 *
 * Every answer states what is true in the present tense and stops there. A page
 * of questions is where hedging collects, and a stranger who learns what the
 * business cannot do yet before they learn what the product does leaves.
 *
 * The three languages are written out rather than translated at run time. A
 * question is a retrieval anchor: someone searches the words they would say,
 * and those words are not a rendering of the English ones.
 */

export interface FaqSection {
  title: string;
  faqs: ArticleFaq[];
}

const EN: FaqSection[] = [
  {
    title: "Getting started",
    faqs: [
      {
        question: "What do I need to start?",
        answer:
          "Two languages, a daily word count, and nothing else. There is no account to create and no card to enter. Pick the language you are learning and the one you already speak, choose how many words a day suits you, and day 1 opens straight away.",
      },
      {
        question: "Which languages can I learn?",
        answer:
          "Thai, Dutch and English, in any pairing. You choose one language to learn and one you already speak, and the sheet puts them side by side in that order. More pairs depend on demand, which is why the day 8 form asks which pair you are on.",
      },
      {
        question: "How long does one day take?",
        answer:
          "About five minutes at the default pace of five words and two sentences. A day that regularly runs past ten minutes is a sign the word count is set too high rather than a sign you are slow, and lowering it is the intended fix.",
      },
      {
        question: "Do I need a printer?",
        answer:
          "No. Every sheet reads on a phone or a laptop, and on screen you can tap a word to hear it spoken by a voice installed on your device. Printing matters when you want the day to happen away from a screen, or when two people are sharing one sheet.",
      },
    ],
  },
  {
    title: "How the sheets work",
    faqs: [
      {
        question: "Why are there two columns and a fold line?",
        answer:
          "So you can hide one language and test yourself on the other. Folding along the line turns reading into recall, which is what actually moves a word into memory. Folded the other way, the same sheet tests the other direction, or a second person learning the opposite way.",
      },
      {
        question: "Can two people use one sheet?",
        answer:
          "Yes, and that is what the two name fields are for. Put a name above each column and each of you folds away your own target language, so the Thai learner and the Dutch learner work from identical rows without either needing a separate page.",
      },
      {
        question: "What is the QR code on a printed sheet for?",
        answer:
          "It opens that exact day on a phone. A printed sheet cannot play audio or record that you finished, so scanning the code takes you to the same day on screen where you can hear the words and tick the day off.",
      },
      {
        question: "Can I print a whole week at once?",
        answer:
          "Yes. The course is released a week at a time and all seven sheets arrive together, so the week page prints as a single job of seven A4 pages. Printing on a Sunday turns seven daily decisions into one weekly one.",
      },
      {
        question: "Why can I change how many words a day I get?",
        answer:
          "Because the word count is a pace control rather than a page-fill setting. All the vocabulary sits in one ordered pool, and the daily count is a window over it, so raising the pace re-cuts the same words into fewer, wider days without changing where you started.",
      },
    ],
  },
  {
    title: "Progress, privacy and cost",
    faqs: [
      {
        question: "Where is my progress stored?",
        answer:
          "In your browser, on the device you are using. There is no account and no server-side profile, so your settings, your ticked-off days and your names stay on the device that made them. Clearing your browser storage clears your progress with it.",
      },
      {
        question: "Does anything I type leave my device?",
        answer:
          "Only an email address, and only if you choose to type one when week 2 arrives. Sheets, names, settings and progress never leave the device. The curriculum is fetched read-only, which means the server is asked for lessons and told nothing about you.",
      },
      {
        question: "What does it cost?",
        answer:
          "Four euro a month for the whole course, at the same price whether one of you learns or five. That covers every day of every week, on screen and printed, with no separate charge for a second learner in the house.",
      },
      {
        question: "Why does week 2 ask for my email?",
        answer:
          "Because someone who finished a week and came back for more is worth hearing from, and because the list decides which language pair is built next. The address is recorded before the rest of the course opens, so closing the tab on the confirmation loses nothing.",
      },
      {
        question: "Can I change my languages after I have started?",
        answer:
          "Yes, from the settings page, and it does not reset anything. The days you have ticked off stay ticked off, and the sheets are rebuilt in the new pair from the same position in the course.",
      },
    ],
  },
];

const NL: FaqSection[] = [
  {
    title: "Beginnen",
    faqs: [
      {
        question: "Wat heb ik nodig om te beginnen?",
        answer:
          "Twee talen, een aantal woorden per dag, en verder niets. Er is geen account aan te maken en geen betaalgegevens in te vullen. Kies de taal die je leert en de taal die je al spreekt, kies hoeveel woorden per dag je aankunt, en dag 1 gaat meteen open.",
      },
      {
        question: "Welke talen kan ik leren?",
        answer:
          "Thai, Nederlands en Engels, in elke combinatie. Je kiest één taal om te leren en één die je al spreekt, en het blad zet ze in die volgorde naast elkaar. Meer combinaties hangen af van de vraag, en daarom vraagt het formulier op dag 8 welk paar je doet.",
      },
      {
        question: "Hoe lang duurt één dag?",
        answer:
          "Ongeveer vijf minuten op het standaardtempo van vijf woorden en twee zinnen. Een dag die steeds langer duurt dan tien minuten betekent dat het aantal woorden te hoog staat, niet dat jij traag bent, en het verlagen ervan is de bedoelde oplossing.",
      },
      {
        question: "Heb ik een printer nodig?",
        answer:
          "Nee. Elk blad leest op een telefoon of laptop, en op het scherm tik je op een woord om het te horen met een stem die al op je apparaat staat. Printen is handig als de dag weg van een scherm moet gebeuren, of als twee mensen één blad delen.",
      },
    ],
  },
  {
    title: "Hoe de bladen werken",
    faqs: [
      {
        question: "Waarom twee kolommen en een vouwlijn?",
        answer:
          "Zodat je één taal kunt verbergen en jezelf op de andere kunt overhoren. Vouwen langs de lijn verandert lezen in ophalen, en dat is wat een woord echt in je geheugen zet. Andersom gevouwen overhoort hetzelfde blad de andere richting, of een tweede persoon die de omgekeerde kant leert.",
      },
      {
        question: "Kunnen twee mensen één blad gebruiken?",
        answer:
          "Ja, en daar zijn de twee naamvelden voor. Zet een naam boven elke kolom en ieder vouwt zijn eigen doeltaal weg, zodat de Thai-leerder en de Nederlands-leerder uit precies dezelfde regels werken zonder dat er een tweede pagina nodig is.",
      },
      {
        question: "Waar is de QR-code op een geprint blad voor?",
        answer:
          "Die opent precies die dag op een telefoon. Een geprint blad kan geen geluid afspelen en niet onthouden dat je klaar bent, dus met de code kom je op dezelfde dag op het scherm, waar je de woorden kunt horen en de dag kunt afvinken.",
      },
      {
        question: "Kan ik een hele week in één keer printen?",
        answer:
          "Ja. De cursus komt per week vrij en alle zeven bladen komen samen binnen, dus de weekpagina print als één opdracht van zeven A4-pagina's. Zondag printen maakt van zeven dagelijkse beslissingen één wekelijkse.",
      },
      {
        question: "Waarom kan ik het aantal woorden per dag veranderen?",
        answer:
          "Omdat het aantal woorden een tempoknop is en geen instelling om de pagina mee te vullen. Alle woordenschat ligt in één geordende voorraad en het dagelijkse aantal is een venster daarover, dus het tempo verhogen knipt dezelfde woorden in minder maar bredere dagen zonder te veranderen waar je begonnen bent.",
      },
    ],
  },
  {
    title: "Voortgang, privacy en kosten",
    faqs: [
      {
        question: "Waar wordt mijn voortgang bewaard?",
        answer:
          "In je browser, op het apparaat dat je gebruikt. Er is geen account en geen profiel op een server, dus je instellingen, je afgevinkte dagen en je namen blijven op het apparaat dat ze gemaakt heeft. Je browseropslag wissen wist je voortgang mee.",
      },
      {
        question: "Verlaat iets wat ik typ mijn apparaat?",
        answer:
          "Alleen een e-mailadres, en alleen als je er zelf een invult wanneer week 2 aanbreekt. Bladen, namen, instellingen en voortgang verlaten het apparaat nooit. Het lesplan wordt alleen opgehaald, wat betekent dat de server om lessen gevraagd wordt en niets over jou te horen krijgt.",
      },
      {
        question: "Wat kost het?",
        answer:
          "Vier euro per maand voor de hele cursus, voor dezelfde prijs of er nu één iemand uit leert of vijf. Dat dekt elke dag van elke week, op het scherm en geprint, zonder aparte kosten voor een tweede leerder in huis.",
      },
      {
        question: "Waarom vraagt week 2 om mijn e-mailadres?",
        answer:
          "Omdat iemand die een week afmaakte en terugkwam voor meer het waard is om van te horen, en omdat de lijst bepaalt welk taalpaar als volgende gebouwd wordt. Het adres wordt vastgelegd voordat de rest van de cursus opengaat, dus het tabblad sluiten bij de bevestiging kost je niets.",
      },
      {
        question: "Kan ik mijn talen wisselen nadat ik begonnen ben?",
        answer:
          "Ja, via de instellingenpagina, en er wordt niets door gereset. De dagen die je hebt afgevinkt blijven afgevinkt, en de bladen worden in het nieuwe paar opnieuw opgebouwd vanaf dezelfde plek in de cursus.",
      },
    ],
  },
];

const TH: FaqSection[] = [
  {
    title: "เริ่มต้นใช้งาน",
    faqs: [
      {
        question: "ต้องมีอะไรบ้างถึงจะเริ่มได้",
        answer:
          "มีแค่สองภาษากับจำนวนคำต่อวัน ไม่ต้องมีอย่างอื่นเลย ไม่ต้องสมัครสมาชิกและไม่ต้องกรอกบัตร เลือกภาษาที่กำลังเรียนกับภาษาที่พูดได้อยู่แล้ว เลือกว่าวันละกี่คำถึงจะพอดี แล้ววันที่ 1 ก็เปิดทันที",
      },
      {
        question: "เรียนภาษาอะไรได้บ้าง",
        answer:
          "มีภาษาไทย ดัตช์ และอังกฤษ จับคู่แบบไหนก็ได้ คุณเลือกหนึ่งภาษาที่จะเรียนกับหนึ่งภาษาที่พูดได้แล้ว ใบงานจะวางทั้งสองไว้คู่กันตามลำดับนั้น ส่วนคู่ภาษาอื่นขึ้นอยู่กับความต้องการ ซึ่งเป็นเหตุผลที่แบบฟอร์มวันที่ 8 ถามว่าคุณเรียนคู่ไหนอยู่",
      },
      {
        question: "หนึ่งวันใช้เวลานานแค่ไหน",
        answer:
          "ประมาณห้านาทีที่จังหวะเริ่มต้นคือวันละห้าคำกับสองประโยค ถ้าวันหนึ่งใช้เวลาเกินสิบนาทีอยู่บ่อย ๆ แปลว่าตั้งจำนวนคำไว้สูงเกินไป ไม่ได้แปลว่าคุณช้า และการลดจำนวนคำลงคือทางแก้ที่ตั้งใจไว้",
      },
      {
        question: "ต้องมีเครื่องพิมพ์ไหม",
        answer:
          "ไม่ต้อง ใบงานทุกแผ่นอ่านบนมือถือหรือแล็ปท็อปได้ และบนหน้าจอคุณแตะที่คำเพื่อฟังเสียงจากเสียงที่ติดตั้งอยู่ในเครื่องได้ การพิมพ์มีประโยชน์ตอนที่อยากให้วันนั้นเกิดขึ้นห่างจากหน้าจอ หรือตอนที่สองคนใช้ใบงานแผ่นเดียวกัน",
      },
    ],
  },
  {
    title: "ใบงานทำงานอย่างไร",
    faqs: [
      {
        question: "ทำไมต้องมีสองคอลัมน์กับเส้นพับ",
        answer:
          "เพื่อให้คุณปิดภาษาหนึ่งไว้แล้วทดสอบอีกภาษาหนึ่งได้ การพับตามเส้นเปลี่ยนการอ่านให้กลายเป็นการนึกออกเอง ซึ่งเป็นสิ่งที่ทำให้คำเข้าไปอยู่ในความจำจริง ๆ ถ้าพับกลับด้าน ใบงานแผ่นเดิมก็ทดสอบอีกทิศทางหนึ่ง หรือให้อีกคนที่เรียนสลับทางใช้ได้",
      },
      {
        question: "สองคนใช้ใบงานแผ่นเดียวกันได้ไหม",
        answer:
          "ได้ และนั่นคือหน้าที่ของช่องใส่ชื่อสองช่อง ใส่ชื่อไว้เหนือแต่ละคอลัมน์ แล้วแต่ละคนก็พับภาษาเป้าหมายของตัวเองเก็บไว้ คนที่เรียนภาษาไทยกับคนที่เรียนภาษาดัตช์จึงทำงานจากบรรทัดเดียวกันได้โดยไม่ต้องใช้คนละหน้า",
      },
      {
        question: "คิวอาร์โค้ดบนใบงานที่พิมพ์ออกมามีไว้ทำอะไร",
        answer:
          "มันเปิดวันนั้นบนมือถือให้ตรงเป๊ะ ใบงานที่พิมพ์แล้วเล่นเสียงไม่ได้และบันทึกไม่ได้ว่าคุณทำเสร็จ การสแกนโค้ดจึงพาคุณไปที่วันเดียวกันบนหน้าจอ ที่ซึ่งฟังเสียงคำและกดว่าทำเสร็จได้",
      },
      {
        question: "พิมพ์ทั้งสัปดาห์ทีเดียวได้ไหม",
        answer:
          "ได้ หลักสูตรเปิดทีละสัปดาห์และใบงานทั้งเจ็ดแผ่นมาพร้อมกัน หน้าสัปดาห์จึงพิมพ์เป็นงานเดียวจำนวนเจ็ดหน้า A4 การพิมพ์ในวันอาทิตย์เปลี่ยนการตัดสินใจเจ็ดครั้งต่อสัปดาห์ให้เหลือครั้งเดียว",
      },
      {
        question: "ทำไมถึงเปลี่ยนจำนวนคำต่อวันได้",
        answer:
          "เพราะจำนวนคำเป็นปุ่มปรับจังหวะ ไม่ใช่การตั้งค่าเพื่อเติมหน้ากระดาษ คำศัพท์ทั้งหมดอยู่ในกองเดียวที่เรียงลำดับไว้แล้ว และจำนวนต่อวันคือกรอบที่เลื่อนไปบนกองนั้น การเพิ่มจังหวะจึงตัดคำชุดเดิมใหม่ให้เป็นวันที่น้อยลงแต่กว้างขึ้น โดยไม่เปลี่ยนจุดที่คุณเริ่มไว้",
      },
    ],
  },
  {
    title: "ความคืบหน้า ความเป็นส่วนตัว และค่าใช้จ่าย",
    faqs: [
      {
        question: "ความคืบหน้าของฉันเก็บไว้ที่ไหน",
        answer:
          "อยู่ในเบราว์เซอร์ บนเครื่องที่คุณใช้อยู่ ไม่มีบัญชีผู้ใช้และไม่มีโปรไฟล์บนเซิร์ฟเวอร์ การตั้งค่า วันที่คุณกดว่าทำเสร็จ และชื่อของคุณจึงอยู่บนเครื่องที่สร้างมันขึ้นมา ถ้าล้างที่เก็บข้อมูลของเบราว์เซอร์ ความคืบหน้าก็หายไปด้วย",
      },
      {
        question: "สิ่งที่ฉันพิมพ์ออกจากเครื่องไหม",
        answer:
          "มีแค่อีเมล และเฉพาะตอนที่คุณเลือกจะกรอกเองเมื่อถึงสัปดาห์ที่ 2 ส่วนใบงาน ชื่อ การตั้งค่า และความคืบหน้าไม่เคยออกจากเครื่อง หลักสูตรถูกดึงมาแบบอ่านอย่างเดียว หมายความว่าเซิร์ฟเวอร์ถูกขอบทเรียนไปเท่านั้น และไม่ได้รับข้อมูลอะไรเกี่ยวกับคุณ",
      },
      {
        question: "ราคาเท่าไร",
        answer:
          "เดือนละสี่ยูโรสำหรับทั้งหลักสูตร ราคาเท่ากันไม่ว่าจะมีคนเรียนคนเดียวหรือห้าคน ครอบคลุมทุกวันของทุกสัปดาห์ ทั้งบนหน้าจอและแบบพิมพ์ โดยไม่คิดเงินเพิ่มสำหรับคนที่สองในบ้าน",
      },
      {
        question: "ทำไมสัปดาห์ที่ 2 ถึงขออีเมล",
        answer:
          "เพราะคนที่เรียนจบหนึ่งสัปดาห์แล้วกลับมาเรียนต่อคือคนที่ควรได้ฟังความเห็น และเพราะรายชื่อนี้เป็นตัวตัดสินว่าคู่ภาษาไหนจะถูกสร้างต่อไป อีเมลถูกบันทึกก่อนที่ส่วนที่เหลือของหลักสูตรจะเปิด การปิดแท็บตอนหน้ายืนยันจึงไม่ทำให้เสียอะไร",
      },
      {
        question: "เริ่มไปแล้วเปลี่ยนภาษาได้ไหม",
        answer:
          "ได้ เปลี่ยนจากหน้าตั้งค่า และไม่มีอะไรถูกรีเซ็ต วันที่คุณกดว่าทำเสร็จยังคงอยู่ และใบงานจะถูกสร้างใหม่ในคู่ภาษาใหม่จากตำแหน่งเดิมในหลักสูตร",
      },
    ],
  },
];

const SECTIONS: Record<Locale, FaqSection[]> = { en: EN, nl: NL, th: TH };

export function faqSections(locale: Locale): FaqSection[] {
  return SECTIONS[locale];
}

/** Every question on the page, flattened in the order they are shown. */
export function listSiteFaqs(locale: Locale): ArticleFaq[] {
  return faqSections(locale).flatMap((section) => section.faqs);
}

/** The English set, for anything that predates the other two languages. */
export const FAQ_SECTIONS = EN;
